import type { Channel, EventDetails, Ticket } from '../types';
import { stripHtml } from './htmlUtils';

export interface WebsiteSchemaOptions {
  siteName: string;
  siteUrl: string;
  description?: string;
  searchUrlTemplate?: string; // e.g. "https://example.com/events?search={search_term}"
}

function toIsoStringFromSeconds(seconds: number | null | undefined): string | undefined {
  if (!seconds || Number.isNaN(seconds)) return undefined;
  return new Date(seconds * 1000).toISOString();
}

function truncate(text: string | undefined | null, maxChars: number): string | undefined {
  if (!text) return undefined;
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxChars) return normalized;
  return `${normalized.slice(0, Math.max(0, maxChars - 1)).trimEnd()}…`;
}

function normalizeCurrency(currency: string | undefined | null): string | undefined {
  if (!currency) return undefined;
  const upper = currency.toUpperCase().trim();
  if (upper === 'TL') return 'TRY';
  return upper;
}

function getMinTicket(tickets: Ticket[] | undefined | null): Ticket | undefined {
  if (!tickets || tickets.length === 0) return undefined;
  return tickets.reduce<Ticket | undefined>((min, t) => {
    if (!min) return t;
    return (t.price ?? 0) < (min.price ?? 0) ? t : min;
  }, undefined);
}

function parseGeo(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function generateWebsiteSchema(options: WebsiteSchemaOptions) {
  const { siteName, siteUrl, description, searchUrlTemplate } = options;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: siteUrl,
  };

  if (description) schema.description = description;

  if (searchUrlTemplate) {
    schema.potentialAction = {
      '@type': 'SearchAction',
      target: searchUrlTemplate,
      'query-input': 'required name=search_term',
    };
  }

  return schema;
}

export function generateOrganizationSchema(params: {
  community: Channel;
  url: string;
  logoUrl?: string;
}) {
  const { community, url, logoUrl } = params;

  const name = community.channelTitle || community.title || 'Community';
  const description = truncate(community.description, 300);

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url,
  };

  if (description) schema.description = description;
  if (logoUrl) schema.logo = logoUrl;

  // If you later store explicit social links on Channel, you can populate schema.sameAs here.
  return schema;
}

export function generateEventSchema(params: {
  event: EventDetails;
  url: string;
  imageUrl?: string;
  organizerName?: string;
  organizerUrl?: string;
}) {
  const { event, url, imageUrl, organizerName, organizerUrl } = params;

  const name = event.eventTitle || event.title || 'Event';
  const description = truncate(stripHtml(event.description || ''), 400);

  const startDate = toIsoStringFromSeconds(event.eventStartDateTime);
  const endDate = toIsoStringFromSeconds(event.eventEndDateTime);

  const isOnline = event.isOnline === '1';

  const attendanceMode = isOnline
    ? 'https://schema.org/OnlineEventAttendanceMode'
    : 'https://schema.org/OfflineEventAttendanceMode';

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name,
    url,
    eventAttendanceMode: attendanceMode,
    eventStatus: 'https://schema.org/EventScheduled',
  };

  if (description) schema.description = description;
  if (startDate) schema.startDate = startDate;
  if (endDate) schema.endDate = endDate;
  if (imageUrl) schema.image = imageUrl;

  if (organizerName || organizerUrl) {
    schema.organizer = {
      '@type': 'Organization',
      ...(organizerName ? { name: organizerName } : {}),
      ...(organizerUrl ? { url: organizerUrl } : {}),
    };
  }

  if (isOnline) {
    const website = event.website ? event.website.trim() : '';
    schema.location = {
      '@type': 'VirtualLocation',
      ...(website ? { url: website.startsWith('http') ? website : `https://${website}` } : {}),
    };
  } else {
    const streetAddress =
      (event.locationAddressDefinition || '').trim() ||
      (event.locationAddress || '').trim() ||
      undefined;
    const lat = parseGeo(event.locationLatitude);
    const lng = parseGeo(event.locationLongitude);

    schema.location = {
      '@type': 'Place',
      ...(streetAddress
        ? {
            address: {
              '@type': 'PostalAddress',
              streetAddress,
            },
          }
        : {}),
      ...(lat !== undefined && lng !== undefined
        ? {
            geo: {
              '@type': 'GeoCoordinates',
              latitude: lat,
              longitude: lng,
            },
          }
        : {}),
    };
  }

  // Offers (tickets)
  const minTicket = getMinTicket(event.tickets);
  if (minTicket) {
    const currency = normalizeCurrency(minTicket.currency) || 'TRY';
    schema.offers = {
      '@type': 'Offer',
      price: String(minTicket.price ?? 0),
      priceCurrency: currency,
      url,
      availability: 'https://schema.org/InStock',
    };
  }

  return schema;
}

export function generatePersonSchema(params: {
  name: string;
  url: string;
  imageUrl?: string;
  jobTitle?: string;
}) {
  const { name, url, imageUrl, jobTitle } = params;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name,
    url,
  };

  if (imageUrl) schema.image = imageUrl;
  if (jobTitle) schema.jobTitle = jobTitle;

  return schema;
}

